import { QUALIFICATION_VALUE_STATUS_LABELS, type QualificationValueStatus } from '@lia/contracts';
import { HelpCircleIcon, LinkSquare02Icon } from '@hugeicons/core-free-icons';
import { Icon } from '../primitives/icon.js';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { Skeleton } from '../primitives/states.js';
import { StatusBadge, type StatusTone } from '../primitives/badge.js';

const STATUS_TONE: Record<QualificationValueStatus, StatusTone> = { confirmed: 'success', declared: 'warning', pending: 'neutral', inferred: 'info' };

export type QualificationSource = 'ai' | 'advisor' | 'crm' | 'form' | 'import';
export type Confidence = 'high' | 'medium' | 'low';

export const QUALIFICATION_SOURCE_LABELS: Record<QualificationSource, string> = {
  ai: 'IA',
  advisor: 'Asesor',
  crm: 'CRM',
  form: 'Formulario',
  import: 'Importación',
};

export const CONFIDENCE_LABELS: Record<Confidence, string> = {
  high: 'Confianza alta',
  medium: 'Confianza media',
  low: 'Confianza baja',
};

export interface QualificationField {
  key: string;
  label: string;
  /** null = unknown. Never substitute 0 or a guess. */
  value: React.ReactNode | null;
  source?: QualificationSource;
  confidence?: Confidence;
  /** Whether the value was confirmed in conversation, only declared (form/CRM), inferred or is still pending. */
  status?: QualificationValueStatus;
  /** Link to the message / call moment supporting the value. */
  evidence?: { label: string; href?: string; onClick?: () => void };
}

export interface QualificationPanelProps {
  fields: readonly QualificationField[];
  title?: string;
  loading?: boolean;
  /** Header actions (e.g. "Editar"). */
  actions?: React.ReactNode;
  className?: string;
}

const CONFIDENCE_DOTS: Record<Confidence, number> = { high: 3, medium: 2, low: 1 };

function ConfidenceMeter({ level }: { level: Confidence }) {
  return (
    <span className={cn('inline-flex items-center gap-1', level === 'low' ? 'text-warning' : 'text-text-secondary')}>
      <span aria-hidden className="inline-flex gap-0.5">
        {[1, 2, 3].map((i) => (
          <span key={i} className={cn('h-2 w-1 rounded-pill', i <= CONFIDENCE_DOTS[level] ? 'bg-current' : 'bg-border-subtle')} />
        ))}
      </span>
      {CONFIDENCE_LABELS[level]}
    </span>
  );
}

/** Qualification facts with value or "Desconocido", source, confidence and evidence link. */
export const QualificationPanel = React.forwardRef<HTMLElement, QualificationPanelProps>(function QualificationPanel(
  { fields, title = 'Calificación', loading, actions, className },
  ref,
) {
  const titleId = React.useId();
  return (
    <section ref={ref} aria-labelledby={titleId} className={cn('grid gap-3', className)}>
      <div className="flex items-center justify-between gap-2">
        <h3 id={titleId} className="text-body-lg font-semibold text-text-primary">
          {title}
        </h3>
        {actions}
      </div>
      {loading ? (
        <div role="status" aria-label="Cargando calificación" className="grid gap-3">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : (
        <dl className="grid divide-y divide-border-subtle">
          {fields.map((f) => (
            <div key={f.key} className="grid gap-1 py-2.5 first:pt-0 last:pb-0">
              <dt className="text-meta font-medium text-text-secondary">{f.label}</dt>
              <dd className="grid gap-1">
                {f.value === null || f.value === undefined || f.value === '' ? (
                  <span className="inline-flex items-center gap-1 text-body italic text-text-secondary">
                    <Icon icon={HelpCircleIcon} aria-hidden className="size-4" />
                    Desconocido
                  </span>
                ) : (
                  <span className="text-body text-text-primary">{f.value}</span>
                )}
                {f.status ? (
                  <span>
                    <StatusBadge tone={STATUS_TONE[f.status]}>{QUALIFICATION_VALUE_STATUS_LABELS[f.status]}</StatusBadge>
                  </span>
                ) : null}
                {f.source || f.confidence || f.evidence ? (
                  <span className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-meta text-text-secondary">
                    {f.source ? <span>Fuente: {QUALIFICATION_SOURCE_LABELS[f.source]}</span> : null}
                    {f.confidence ? <ConfidenceMeter level={f.confidence} /> : null}
                    {f.evidence ? (
                      f.evidence.href ? (
                        <a
                          href={f.evidence.href}
                          className="inline-flex items-center gap-1 font-medium text-brand-primary underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-focus"
                        >
                          {f.evidence.label}
                          <Icon icon={LinkSquare02Icon} aria-hidden className="size-3" />
                        </a>
                      ) : (
                        <button
                          type="button"
                          onClick={f.evidence.onClick}
                          className="font-medium text-brand-primary underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-focus"
                        >
                          {f.evidence.label}
                        </button>
                      )
                    ) : null}
                  </span>
                ) : null}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
});
